import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";

import * as Yup from "yup";
import FormWrapper from "src/components/FormWrapper";
import { FormProvider, RHFTextField } from "src/components/hook-form";
import RHFAutocomplete from "src/components/hook-form/RHFAutocomplete";
import { restrict } from "src/components/restrict";
import { addProductInventory, masterGetData } from "src/pages/api";

const schema = Yup.object().shape({
  productName: Yup.object().nullable().required("Required"),
  publicationName: Yup.object().nullable().required("Required"),
  qty: Yup.number()
    .nullable()
    .typeError("Required")
    .required("Required")
    .min(1, "Minimum value should be 1"),
});

export default function AddInventory({ onClose, row }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      productName: null,
      publicationName: null,
      qty: "",
    },
  });

  const { handleSubmit, reset } = methods;

  // Fetch Publisher List
  const { data: publisherData } = useQuery({
    queryKey: ["getData", "publisher", 0, 100],
    queryFn: masterGetData,
  });
  const { data: productList } = useQuery({
    queryKey: ["getData", "products", 0, 5000],
    queryFn: masterGetData,
  });

  const mutation = useMutation((req) => addProductInventory({ req }), {
    onSuccess: (success) => {
      enqueueSnackbar(success?.message ?? "Inventory successfully added", {
        variant: "success",
      });
      queryClient.invalidateQueries(["getInventory"]); // Invalidate inventory query to refresh data
      onClose(); // Close modal after successful save
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || "Failed to add inventory", {
        variant: "error",
      });
    },
  });
  

  const onSubmit = (data) => {
    const req = {
      productID: data.productName?.id,
      qty: +data.qty,
      publisherID: data.publicationName?.id,
    };
    mutation.mutate(req);
  };

  useEffect(() => {
    if (row && productList?.data?.[0]?.data) {
      reset({
        productName:
          productList?.data?.[0]?.data.find((p) => p.id === row.productID) ||
          null,
        qty: "",
        publicationName: null,
      });
    }
  }, [row, productList]);

  return (
    <FormWrapper
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      onReset={() => {
        reset();
      }}
      loading={mutation.isLoading}
      maxWidth={"sm"}
      fullWidth
      title="Add Inventory"
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <RHFAutocomplete
              name="productName"
              label="Product Name *"
              size="large"
              required
              option={productList?.data || []}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <RHFAutocomplete
              name="publicationName"
              label="Publisher Name *"
              size="large"
              required
              option={publisherData?.data || []}
              getOptionLabel={(option) => option?.publisherName || ""}
              isOptionEqualToValue={(option, value) =>
                option?.publisherName === value?.publisherName
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <RHFTextField
              name="qty"
              label="Qty"
              size="large"
              inputProps={{ maxLength: 5 }}
              onInput={restrict.digits}
            />
          </Grid>
        </Grid>
      </FormProvider>
    </FormWrapper>
  );
}
